import React, { useCallback, useEffect } from 'react';

import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import { Drawer } from 'rsuite';

import { PageHeader, Pagination, DrawerSeparator } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import Search from '../../components/Search/Search.component';
import CreateRoleModal from './CreateRoleModal';

import useComponentState from '../../utils/useComponentState';
import { searchAdministrators } from '../../api/auth';
import { createRole } from '../../constants/createRole';
import {
  MERCHANT_SCOPED_ROLES,
  CLIENT_SCOPED_ROLES,
  getRoleLabel,
  getRowMerchants,
  getRowClient,
} from '../../constants/roles';

import styles from './createUser.module.scss';

const CreateUser = (props) => {
  const [state, setState] = useComponentState({
    showCreateRoleModal: false,
    tableData: [],
    loading: false,
    error: '',
    currentPage: 0,
    totalPages: 0,
    email: '',
    merchantId: '',
    showDrawer: false,
    drawerType: null,
    drawerData: null,
    drawerMerchants: [],
    drawerClient: null,
  });

  const { token, details } = props.auth.auth;

  const fetchAdministrators = useCallback(
    async (page, email, merchantId) => {
      setState({ loading: true, error: '' });
      try {
        const response = await searchAdministrators(token, {
          page,
          pageSize: createRole.pageSize,
          email,
          merchantId,
        });
        setState({
          tableData: response.list || [],
          totalPages: response.totalPageCount || 0,
        });
      } catch (err) {
        console.error('searchAdministrators failed:', err);
        setState({
          error: getRoleLabel(
            err?.message || createRole.messages.fetchAdministratorsFailed
          ),
          tableData: [],
          totalPages: 0,
        });
      }
      setState({ loading: false });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  );

  useEffect(() => {
    fetchAdministrators(state.currentPage, state.email, state.merchantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage]);

  const handleSearch = (email, merchantId) => {
    setState({ currentPage: 0 });
    fetchAdministrators(0, email, merchantId);
  };

  const handlePagination = (page) => {
    setState({ currentPage: page });
  };

  const handleOnClick = (id) => {
    const rowData = state.tableData.find((data) => data.id === id);
    if (!rowData) return;

    if (MERCHANT_SCOPED_ROLES.includes(rowData.role)) {
      const merchants = getRowMerchants(rowData);
      if (merchants.length === 0) return;

      setState({
        showDrawer: true,
        drawerType: 'merchant',
        drawerData: rowData,
        drawerMerchants: merchants,
      });
      return;
    }

    if (CLIENT_SCOPED_ROLES.includes(rowData.role)) {
      const client = getRowClient(rowData);
      if (!client) return;

      setState({
        showDrawer: true,
        drawerType: 'client',
        drawerData: rowData,
        drawerClient: client,
      });
    }
  };

  const handleDrawerOnHide = () => {
    setState({
      showDrawer: false,
      drawerType: null,
      drawerData: null,
      drawerMerchants: [],
      drawerClient: null,
    });
  };

  const handleUserCreated = () => {
    setState({ currentPage: 0 });
    fetchAdministrators(0, state.email, state.merchantId);
  };

  const getGreetlyStatus = (merchant) => {
    if (typeof merchant.isGreetlyEnable !== 'boolean') return null;
    return merchant.isGreetlyEnable
      ? createRole.greetlyStatus.enabled
      : createRole.greetlyStatus.disabled;
  };

  const renderMerchantFields = (merchant) => {
    const fields = [
      { label: 'ID', value: merchant.id },
      { label: 'Name', value: merchant.name },
      { label: 'Category', value: merchant.category },
      { label: 'Status', value: merchant.status },
      { label: createRole.labels.greetly, value: getGreetlyStatus(merchant) },
    ];

    return fields.map(({ label, value }) => (
      <div className={styles.credential_row} key={label}>
        <span className={styles.credential_label}>{label}</span>
        <span className={styles.credential_value}>
          {value === null || value === undefined || value === ''
            ? 'N/A'
            : String(value)}
        </span>
      </div>
    ));
  };

  const renderClientFields = (client) => {
    const fields = [
      { label: 'ID', value: client.id },
      { label: 'Name', value: client.name },
    ];

    return fields.map(({ label, value }) => (
      <div className={styles.credential_row} key={label}>
        <span className={styles.credential_label}>{label}</span>
        <span className={styles.credential_value}>
          {value === null || value === undefined || value === ''
            ? 'N/A'
            : String(value)}
        </span>
      </div>
    ));
  };

  const renderClientDrawer = () => {
    const { drawerClient } = state;

    return (
      <div className={styles.drawer_root}>
        <DrawerSeparator text={createRole.titles.clientDetails} />
        {!drawerClient ? (
          <p>{createRole.notFoundMessage}</p>
        ) : (
          <div className={`${styles.credentials_box} ${styles.merchant_card}`}>
            {renderClientFields(drawerClient)}
          </div>
        )}
      </div>
    );
  };

  const renderMerchantDrawer = () => {
    const { drawerMerchants } = state;

    return (
      <div className={styles.drawer_root}>
        <DrawerSeparator text={createRole.titles.merchantDetails} />
        {drawerMerchants.length === 0 ? (
          <p>{createRole.notFoundMessage}</p>
        ) : (
          drawerMerchants.map((merchant, index) => (
            <div
              className={`${styles.credentials_box} ${styles.merchant_card}`}
              key={merchant.id}
            >
              {drawerMerchants.length > 1 && (
                <div className={styles.credential_row}>
                  <span className={styles.credential_label}>{`#${
                    index + 1
                  }`}</span>
                </div>
              )}
              {renderMerchantFields(merchant)}
            </div>
          ))
        )}
      </div>
    );
  };

  const renderDrawer = () =>
    state.drawerType === 'client' ? renderClientDrawer() : renderMerchantDrawer();

  return (
    <div className={styles.root_div}>
      <PageHeader text={createRole.titles.createUser} />

      <div className={styles.filter_form}>
        <div className={styles.filter_input}>
          <Search
            placeholder={createRole.placeholders.searchByEmail}
            handleValueFromParent
            value={state.email}
            onChange={(email) => setState({ email })}
            onBtnPress={(email) => handleSearch(email, state.merchantId)}
            onClearPress={() => handleSearch('', state.merchantId)}
          />
        </div>
        <div className={styles.filter_input}>
          <Search
            placeholder={createRole.placeholders.searchByMerchantId}
            handleValueFromParent
            value={state.merchantId}
            onChange={(merchantId) => setState({ merchantId })}
            onBtnPress={(merchantId) => handleSearch(state.email, merchantId)}
            onClearPress={() => handleSearch(state.email, '')}
          />
        </div>
        <Button
          type='button'
          className={styles.create_role_btn}
          onClick={() => setState({ showCreateRoleModal: true })}
        >
          {createRole.buttonText.createRole}
        </Button>
      </div>

      <DataTable
        tableData={state.tableData}
        tableheading={{ column: createRole.tableColumns }}
        handleOnClick={handleOnClick}
        tag={createRole.tag}
        fetchError={state.error}
        search_filter_loading={state.loading}
        notFoundMessage={createRole.notFoundMessage}
      />

      <Pagination
        pageCount={state.totalPages}
        onChange={handlePagination}
        currentPage={state.currentPage}
      />

      <CreateRoleModal
        show={state.showCreateRoleModal}
        onHide={() => setState({ showCreateRoleModal: false })}
        token={token}
        currentUserRole={details.role}
        onCreated={handleUserCreated}
      />

      <Drawer show={state.showDrawer} onHide={handleDrawerOnHide}>
        {state.drawerData && renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(CreateUser);
